import logger from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Mail, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailPopup = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Don't show popup on admin routes or immersive experiences
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isImmersiveRoute = location.pathname === '/ship';
  const isPurchaseRoute = location.pathname === '/cart' || location.pathname.startsWith('/checkout') || location.pathname === '/gift-finder';
  const isAuthRoute = ['/login', '/register', '/forgot-password', '/reset-password'].some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    // Don't show on admin pages, immersive 3D experiences, or auth pages
    if (isAdminRoute || isImmersiveRoute || isAuthRoute) return;
    
    // Check if already subscribed or dismissed recently (within 24 hours)
    const dismissed = localStorage.getItem('emailPopupDismissed');
    const subscribed = localStorage.getItem('emailSubscribed');
    const dismissedTime = localStorage.getItem('emailPopupDismissedTime');
    
    // If dismissed within last 24 hours, don't show
    if (dismissedTime) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) return;
    }
    
    if (!dismissed && !subscribed) {
      // Show popup after 15 seconds to let users explore first (never alongside another popup)
      const timer = setTimeout(() => {
        if (!window.__selahPopupOpen) {
          window.__selahPopupOpen = true;
          setIsOpen(true);
        }
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [isAdminRoute, isImmersiveRoute, isAuthRoute]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;

    setLoading(true);
    try {
      // Send to A.I_SHA Email List backend
      const response = await fetch(`${API_URL}/api/aisha/email-list/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          source: 'popup',
          tags: ['welcome_popup', 'discount_seeker']
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('emailSubscribed', 'true');
        setSubmitted(true);
        toast.success(`Welcome! Your 10% discount code: ${data.discount_code || 'WELCOME10'}`);
      } else {
        toast.error(data.message || 'Something went wrong');
      }
    } catch (error) {
      logger.error('Subscription error:', error);
      // Fallback to localStorage if backend fails
      localStorage.setItem('emailSubscribed', 'true');
      setSubmitted(true);
      toast.success('Welcome! Your 10% discount code: WELCOME10');
    }
    setLoading(false);
  };

  const handleClose = () => {
    window.__selahPopupOpen = false;
    setIsOpen(false);
    localStorage.setItem('emailPopupDismissed', 'true');
    localStorage.setItem('emailPopupDismissedTime', Date.now().toString());
  };

  if (!isOpen || isPurchaseRoute || isAuthRoute) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="email-popup">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-10"
          data-testid="close-popup-btn"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Gradient header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white text-center">
          <Gift className="h-12 w-12 mx-auto mb-3" />
          <h2 className="text-2xl font-bold">Get 10% Off!</h2>
          <p className="text-indigo-100 text-sm mt-1">Join our exclusive list</p>
        </div>

        <div className="p-6">
          {!submitted ? (
            <>
              <p className="text-slate-600 text-center mb-4">
                Subscribe to get exclusive deals, new arrivals, and your first order discount!
              </p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  data-testid="subscribe-btn"
                >
                  Get My 10% Off
                </Button>
              </form>
              <p className="text-xs text-slate-400 text-center mt-3">
                No spam, unsubscribe anytime.
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Gift className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">You&apos;re In!</h3>
              <p className="text-slate-600 mb-4">Use code at checkout:</p>
              <div className="bg-slate-100 rounded-lg p-3 font-mono text-lg font-bold text-indigo-600">
                WELCOME10
              </div>
              <Button 
                onClick={handleClose}
                className="mt-4 bg-indigo-600 hover:bg-indigo-700"
              >
                Start Shopping
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailPopup;
